/**
 * Shared route & label helpers for public-facing "experiences"
 * (kurser, event, aktiviteter) listed on producer and place pages.
 *
 * Single source of truth — use everywhere instead of hard-coding paths.
 */
import { activityDetailPath } from '@/lib/paths';

export type ExperienceType =
  | 'course'
  | 'workshop'
  | 'event'
  | 'dinner'
  | 'trip'
  | 'excursion'
  | 'outing'
  | 'experience'
  | (string & {});

/**
 * Swedish UI label for an experience type.
 */
export function experienceLabel(type?: ExperienceType): string {
  const t = (type ?? '').toLowerCase();
  switch (t) {
    case 'course':
    case 'workshop':
      return 'Kurs';
    case 'event':
    case 'dinner':
      return 'Aktivitet';
    case 'trip':
    case 'excursion':
    case 'outing':
      return 'Utflykt';
    default:
      return 'Upplevelse';
  }
}

interface ExperienceRoutable {
  type?: ExperienceType;
  kind?: string;
  slug?: string | null;
  id?: string;
}

/**
 * Public detail-page href, type-aware.
 * Falls back to "/" if neither slug nor id is available.
 */
export function experienceHref(x: ExperienceRoutable): string {
  const t = ((x.type ?? x.kind) ?? '').toLowerCase();
  const identifier = x.slug || x.id;
  if (!identifier) return '/';

  return activityDetailPath(identifier);
}

// ---------- Dev self-check ----------
if (import.meta.env.DEV) {
  console.assert(experienceLabel('course') === 'Kurs', 'label: course');
  console.assert(experienceLabel('outing') === 'Utflykt', 'label: outing');
  console.assert(experienceLabel('event') === 'Aktivitet', 'label: event');
  console.assert(experienceLabel(undefined) === 'Upplevelse', 'label: default');

  console.assert(experienceHref({ type: 'course', id: '1' }) === '/event/1', 'href: course');
  console.assert(experienceHref({ type: 'outing', slug: 'abc' }) === '/event/abc', 'href: outing');
  console.assert(experienceHref({ kind: 'event', id: '2' }) === '/event/2', 'href: event via kind');
  console.assert(experienceHref({}) === '/', 'href: no id/slug');
}
